import React, { useEffect, useMemo, useState } from 'react'
import debounce from 'lodash/debounce'
import api from '../../api'
import { SoundSearchContext, EMPTY_SOUND_RESULT } from '../../services/search/SoundSearchContext'

const HITS_PER_CATEGORY = 500
const DEBOUNCE_TIME = 1000

/**
 * Standalone search provider for the Sound Library (EditorSoundLibraryPage).
 *
 * Owns all sound search coupling:
 *   - one request per active category, refetched when the query changes
 *     (debounced, so typing doesn't send a request per keystroke)
 *   - the per-category totals shown in the category nav
 *
 * Swapping the sound search engine requires editing only this file.
 *
 * Categories are named rather than expressed as filters: the API resolves what
 * 'yourSounds' or 'EFL' means from the session, so the client no longer needs
 * the requester's repo list or the subject ids to search.
 *
 * Props:
 *   categories {string[]} Categories to fetch results for. The Sound Library
 *                         shows one while browsing and two while searching.
 *   query      {string}   Free text; empty while browsing.
 *   children   {ReactNode}
 */
function SoundSearchProviderContainer({ categories, query, children }) {
  const [counts, setCounts] = useState({})
  const [resultsByCategory, setResultsByCategory] = useState({})
  const [isSearching, setIsSearching] = useState(false)
  const [debouncedQuery, setDebouncedQuery] = useState(query)

  const setDebouncedQueryLater = useMemo(() => debounce(
    setDebouncedQuery,
    DEBOUNCE_TIME,
    { leading: false },
  ), [])

  useEffect(() => () => setDebouncedQueryLater.cancel(), [setDebouncedQueryLater])

  useEffect(() => {
    // Clearing the search returns to browsing straight away.
    if (query === '') {
      setDebouncedQueryLater.cancel()
      setDebouncedQuery('')
    } else {
      setDebouncedQueryLater(query)
    }
  }, [query, setDebouncedQueryLater])

  // `categories` is a fresh array on every render, so depend on its contents.
  const categoryKey = categories.join(',')

  useEffect(() => {
    let cancelled = false
    api.fetch('/search/sounds/counts')
      .then((result) => {
        if (cancelled) return
        setCounts(result)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    const activeCategories = categoryKey ? categoryKey.split(',') : []
    if (!activeCategories.length) return undefined
    // The page switches `categories` to the search ones on the first keystroke,
    // but `debouncedQuery` is still '' until typing pauses. Skip fetching until
    // it catches up, or we'd search both categories for an empty string.
    if (query !== debouncedQuery) return undefined

    // Cancelling on cleanup means a slow response for an earlier query can
    // never overwrite results for a newer one.
    let cancelled = false
    setIsSearching(true)

    Promise.all(activeCategories.map((category) => (
      api.fetch('/search/sounds', { category, query: debouncedQuery, limit: HITS_PER_CATEGORY })
        .then((result) => {
          if (cancelled) return
          setResultsByCategory((previous) => ({ ...previous, [category]: result }))
        })
        // leave whatever is on screen rather than flashing to empty
        .catch(() => {})
    ))).then(() => {
      if (cancelled) return
      setIsSearching(false)
    })

    return () => { cancelled = true }
  }, [categoryKey, query, debouncedQuery])

  // Count a pending debounced query as searching so the spinner shows while typing.
  const isQueryPending = query !== debouncedQuery

  const value = useMemo(() => ({
    isSearching: isSearching || isQueryPending,
    counts,
    getResults: (category) => resultsByCategory[category] || EMPTY_SOUND_RESULT,
  }), [isSearching, isQueryPending, counts, resultsByCategory])

  return (
    <SoundSearchContext.Provider value={value}>
      {children}
    </SoundSearchContext.Provider>
  )
}

export default SoundSearchProviderContainer
